import type { DateTime } from 'luxon';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { convertSelectionToDateTimeWithScale } from '../Timeline.utils';
// import { useSelectionContainer } from '@air/react-drag-to-select';
import { useSelectionContainer } from './drag-select/useSelectionContainer';
import { TimelineContext } from './Timeline.Context';
import styles from './Timeline.Selection.module.scss';

type Box = { top: number; left: number; height: number; width: number };

const EMPTY_POSITION: Box = { top: 0, height: 0, left: 0, width: 0 };

type SelectionRange = { started_at: DateTime; ended_at: DateTime };

type SelectionProps = {
  /** Returns start/end time of selection (assuming `Timeline.fromDateTime` is set ) */
  onSelectionEnd?: (params: {
    action: 'add' | 'remove';
    started_at: DateTime;
    ended_at: DateTime;
  }) => void;
  snapToMinutes?: number;
  /**
   * Narrows the range a gesture may claim. Applied to the drawn overlay as well
   * as to the reported range, so the preview shows exactly what the drag will
   * produce — on a scale that compresses parts of the axis, a gesture reaching
   * across a compressed stretch must not paint over time it cannot claim.
   * Return `null` to make the gesture a no-op.
   */
  clipRange?: (range: SelectionRange) => SelectionRange | null;
};

/**
 * Allow click-to-select across the Timeline
 * `@air/react-drag-to-select` is doing all the heavy lifting here
 * @note Consider the existing hooks, but discuss with @0rvar about preventDefault stuff.
 */
export const TimelineSelection = (props: SelectionProps) => {
  const { snapToMinutes = 1, clipRange } = props;
  // Position & size of the selection box. This state only drives the rendered
  // overlay: it is written from `onSelectionChange`, which the drag-select hook
  // defers to the next animation frame, so it lags the gesture by up to a
  // frame. The range we report comes from the box handed to `onSelectionEnd`
  // instead — a fast drag can finish before that frame ever runs.
  const [position, setPosition] = useState(EMPTY_POSITION);
  // Whether a gesture is in flight. Frames queued during a drag can run after
  // it ended; this stops them repainting the overlay with dead geometry.
  const isSelectingRef = useRef(false);
  const {
    fromDateTime,
    scrollAreaRef,
    orientation,
    pxPerMinute,
    scale,
    isDragMode,
    isAltKeyHeld,
    setIsDragMode,
  } = useContext(TimelineContext);

  // Viewport-relative selection box → position within the scrollable content.
  // Reads the container's viewport offset live rather than caching it at drag
  // start: the page can scroll under a gesture (anything that scrolls the
  // timeline into view when the drag begins), and a cached offset would then
  // place the range wherever the container used to be.
  const toContentPosition = (selection: Box): Box => {
    const { scrollTop = 0, scrollLeft = 0 } = scrollAreaRef || {};
    const { top = 0, left = 0 } = scrollAreaRef?.getBoundingClientRect() || {};
    return {
      top: selection.top + scrollTop - top,
      left: selection.left + scrollLeft - left,
      height: selection.height,
      width: selection.width,
    };
  };

  const { DragSelection } = useSelectionContainer({
    eventsElement: scrollAreaRef,
    shouldStartSelecting: eventTarget => {
      // Prevent dragging on inputs and other opted-out surfaces. `Element`, not
      // `HTMLElement`: a press often lands on an SVG icon inside the opted-out
      // block, and SVG nodes are not HTMLElements.
      if (eventTarget instanceof Element && eventTarget.closest('[data-no-drag-select]')) {
        return false;
      }
      // Resizable timestamps identify as `data-timestamp-adjuster` to block selection
      const isAdjustHandle = (eventTarget as HTMLElement)?.closest('[data-timestamp-adjuster]');
      return !isDragMode && !isAdjustHandle;
    },
    onSelectionChange: selection => {
      if (!isSelectingRef.current) return;
      setPosition(toContentPosition(selection));
    },
    onSelectionStart: () => {
      isSelectingRef.current = true;
      setIsDragMode(true);
    },
    onSelectionEnd: (_event, selection) => {
      isSelectingRef.current = false;
      setIsDragMode(false);
      setPosition(EMPTY_POSITION);
      if (!selection) return;

      const finalPosition = toContentPosition(selection);
      if (finalPosition.width > pxPerMinute || finalPosition.height > pxPerMinute) {
        const { started_at, ended_at } = convertSelectionToDateTimeWithScale({
          position: finalPosition,
          orientation,
          scale,
          fromDateTime,
          snapToMinutes,
        });
        // A registered drag whose endpoints snap to the same tick still means
        // "select here": round it up to the smallest legal range instead of
        // silently dropping the gesture.
        const minimalEndedAt =
          started_at.toMillis() === ended_at.toMillis()
            ? started_at.plus({ minutes: snapToMinutes })
            : ended_at;
        const clipped = clipRange
          ? clipRange({ started_at, ended_at: minimalEndedAt })
          : { started_at, ended_at: minimalEndedAt };
        if (!clipped) return;
        props.onSelectionEnd?.({
          started_at: clipped.started_at,
          ended_at: clipped.ended_at,
          action: isAltKeyHeld ? 'remove' : 'add',
        });
      }
    },
    // Don't actually render the selection box, we'll draw our own UI
    selectionProps: { style: { display: 'none' } },
  });

  // The overlay is drawn from the snapped *times* mapped back through the
  // scale, so it covers exactly the range the gesture will report — including
  // across a compressed stretch of the day, where equal spans of time are not
  // equal spans of pixels. `clipRange` is applied here too: the preview never
  // washes over time the drop will not claim.
  const style = useMemo((): React.CSSProperties | null => {
    if (!isDragMode) return null;
    const converted = convertSelectionToDateTimeWithScale({
      position,
      orientation,
      scale,
      fromDateTime,
      snapToMinutes,
    });
    const range = clipRange ? clipRange(converted) : converted;
    if (!range) return null;
    const startPx = scale.toPx(range.started_at);
    const sizePx = scale.toPx(range.ended_at) - startPx;
    return orientation === 'vertical'
      ? { top: startPx, height: sizePx, left: 0, right: 0 }
      : { top: 0, bottom: 0, left: startPx, width: sizePx };
  }, [isDragMode, orientation, position, scale, fromDateTime, snapToMinutes, clipRange]);

  return (
    <>
      <DragSelection />
      {style && (
        <div
          className={styles.selectedArea}
          style={style}
          data-testid="timeline_selection_overlay"
        />
      )}
    </>
  );
};
TimelineSelection.displayName = 'Timeline.Selection';
